import {
  type HttpFailureKind,
  isHttpError,
  isHttpRequestError,
  request,
} from '@ns/http-request';

import { TESSA_OBO_ASSERTION_HEADER } from './constants';
import {
  type CurrentUserLicense,
  isCurrentUserLicenses,
  isTessaCurrentUserLicensesModeResponse,
  TESSA_CURRENT_USER_LICENSES_MODE_ROUTE_PATH,
  TESSA_CURRENT_USER_LICENSES_ROUTE_PATH,
} from './current-user-licenses';
import {
  getTessaUserAssertion,
  type TessaUserAssertionDependencies,
} from './tessa-user-assertion';
import { compareLicenseEntries } from './user-licenses';

export interface CurrentUserLicensesLoaderDependencies
  extends TessaUserAssertionDependencies {
  discoveryApi: {
    getBaseUrl(pluginId: string): Promise<string>;
  };
  fetchApi: {
    fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  };
}

export interface CurrentUserLicensesLoaderIdentityApi {
  getBackstageIdentity(): Promise<{ userEntityRef: string }>;
}

export type CurrentUserLicensesResult =
  | { status: 'disabled' }
  | { licenses: CurrentUserLicense[]; status: 'ok' };

type TessaMode = 'live' | 'dummy' | 'disabled';

async function getMode(
  dependencies: CurrentUserLicensesLoaderDependencies,
  baseUrl: string,
  signal: AbortSignal,
): Promise<TessaMode> {
  const response = await request({
    baseUrl,
    cache: 'no-store',
    fetch: dependencies.fetchApi.fetch,
    signal,
    timeout: 10_000,
    url: TESSA_CURRENT_USER_LICENSES_MODE_ROUTE_PATH,
    validate: {
      response: body => {
        if (!isTessaCurrentUserLicensesModeResponse(body)) {
          throw new Error('Invalid current-user Tessa license mode response.');
        }
        return body;
      },
    },
  });
  return response.body.mode;
}

async function getAssertion(
  dependencies: CurrentUserLicensesLoaderDependencies,
  mode: TessaMode,
): Promise<string | undefined> {
  if (mode !== 'live') return undefined;

  const assertion = await getTessaUserAssertion(dependencies);
  if (!assertion) {
    throw new Error('Tessa OBO client ID is not configured.');
  }
  return assertion;
}

async function getLicenses(
  dependencies: CurrentUserLicensesLoaderDependencies,
  baseUrl: string,
  signal: AbortSignal,
  assertion: string | undefined,
): Promise<CurrentUserLicense[]> {
  const response = await request({
    baseUrl,
    cache: 'no-store',
    fetch: dependencies.fetchApi.fetch,
    headers: assertion
      ? { [TESSA_OBO_ASSERTION_HEADER]: assertion }
      : undefined,
    retry: { maxAttempts: 2 },
    signal,
    timeout: 15_000,
    url: TESSA_CURRENT_USER_LICENSES_ROUTE_PATH,
    validate: {
      response: body => {
        if (!isCurrentUserLicenses(body)) {
          throw new Error('Invalid current-user Tessa licenses response.');
        }
        return body;
      },
    },
  });
  return [...response.body].sort(compareLicenseEntries);
}

/** A failed load, described by its `@ns/http-request` failure kind and, for an HTTP answer, its status. */
export interface CurrentUserLicensesFailure {
  readonly kind: HttpFailureKind;
  readonly status?: number;
}

/**
 * Describes an error {@link loadCurrentUserLicenses} rejected with, so a
 * caller can classify it without depending on `@ns/http-request` itself.
 *
 * @remarks
 * The shape matches `Failure` in `@ns/plugin-picker-shared`, so a picker can
 * hand it to that package's failure policy as it is.
 *
 * @returns The description, or `undefined` for an error that is no HTTP
 * failure, such as a failed Microsoft sign-in.
 */
export function describeCurrentUserLicensesFailure(
  error: unknown,
): CurrentUserLicensesFailure | undefined {
  if (isHttpRequestError(error)) return { kind: 'http', status: error.status };
  if (isHttpError(error)) return { kind: error.failureKind };
  return undefined;
}

/**
 * Loads the signed-in user's active Tessa licenses: mode, then (in live mode)
 * the user's Tessa token, then the list. Failures reject. HTTP failures keep
 * their `@ns/http-request` type, and an answer of the wrong shape rejects as a
 * `validation` failure; {@link describeCurrentUserLicensesFailure} describes
 * both. An abort
 * also rejects: with the signal's reason between steps, or with a
 * `RequestAbortError` during a request. Callers tell it apart by
 * `signal.aborted`, not by the error.
 *
 * With `identityApi`, the signed-in user is checked between the steps. When it
 * changed, the result is `{ status: 'identity-changed' }` and the caller should
 * load again.
 */
export async function loadCurrentUserLicenses(
  dependencies: CurrentUserLicensesLoaderDependencies & {
    identityApi: CurrentUserLicensesLoaderIdentityApi;
  },
  signal: AbortSignal,
): Promise<CurrentUserLicensesResult | { status: 'identity-changed' }>;

export async function loadCurrentUserLicenses(
  dependencies: CurrentUserLicensesLoaderDependencies,
  signal: AbortSignal,
): Promise<CurrentUserLicensesResult>;

export async function loadCurrentUserLicenses(
  dependencies: CurrentUserLicensesLoaderDependencies & {
    identityApi?: CurrentUserLicensesLoaderIdentityApi;
  },
  signal: AbortSignal,
): Promise<CurrentUserLicensesResult | { status: 'identity-changed' }> {
  const { identityApi } = dependencies;
  const getEntityRef = async (): Promise<string | undefined> => {
    const identity = await identityApi?.getBackstageIdentity();
    return identity?.userEntityRef;
  };
  const originalEntityRef = await getEntityRef();
  const identityHasChanged = async (): Promise<boolean> =>
    (await getEntityRef()) !== originalEntityRef;
  signal.throwIfAborted();

  const baseUrl = await dependencies.discoveryApi.getBaseUrl('catalog');
  signal.throwIfAborted();

  const mode = await getMode(dependencies, baseUrl, signal);
  signal.throwIfAborted();
  if (await identityHasChanged()) return { status: 'identity-changed' };
  if (mode === 'disabled') return { status: 'disabled' };

  const assertion = await getAssertion(dependencies, mode);
  signal.throwIfAborted();
  if (await identityHasChanged()) return { status: 'identity-changed' };

  const licenses = await getLicenses(dependencies, baseUrl, signal, assertion);
  signal.throwIfAborted();
  if (await identityHasChanged()) return { status: 'identity-changed' };
  return { licenses, status: 'ok' };
}
